import * as React from "react";
import { Route, ScreenTransitionMode } from "@core-ui/interfaces/Router";

export interface NavigateToOptions {
    errorCallback?: VoidFunction;
    onTransitionComplete?: VoidFunction;
    onTransitionExit?: VoidFunction;
}

export interface RouteContextValue<T extends string> {
    navigateTo: <P = {}>(
        name: T,
        data?: P,
        replace?: boolean,
        /**
         * Backward compatible:
         * - pass a function → treated as errorCallback (existing callers)
         * - pass an object → { errorCallback?, onTransitionComplete?, onTransitionExit? }
         *
         * `onTransitionComplete` fires when the entering route finishes entering,
         * `onTransitionExit` when the leaving route finishes exiting. Both may be
         * supplied; ordering depends on the transition mode (`in-out` enters first).
         */
        errorCallbackOrOptions?: VoidFunction | NavigateToOptions,
    ) => void;
    goBack: () => void;
    canGoBack: (numberOfPagesToGoBack?: number) => boolean;
    route?: Route<T>;
    isForward: boolean;
    /**
     * Flushes a pending `navigateTo(..., { onTransitionComplete })` callback
     * (at most once; safe if called again with nothing queued).
     *
     * Why it exists: QC nav handlers register work that must run after the
     * destination screen is visible (webview height / transparent bg). That
     * callback is stored on MiniRouter and flushed from ScreenTransition
     * `onEntered`. Nested CartMain also exposes the same flush so QC ↔ MRO
     * sibling hops (same outer CartMain key, `preservePendingTransitionOnSiblingNav`)
     * still complete when only the nested transition runs — the outer shell does not.
     *
     * There is no `notifyExitComplete`: exit timing (`onExited`) is unused for these
     * callbacks because they must not fire while the previous screen is still leaving.
     */
    notifyTransitionComplete?: VoidFunction;
    /**
     * Exit counterpart of `notifyTransitionComplete`, flushing a pending
     * `navigateTo(..., { onTransitionExit })` callback. Flushed independently, so a nav
     * that registers both callbacks still runs each exactly once.
     */
    notifyTransitionExit?: VoidFunction;
    /** Defaults to `"in-out"` in context; callers may omit. */
    transitionMode?: ScreenTransitionMode;
}

export const RouteContext = React.createContext<RouteContextValue<string>>({
    navigateTo: () => void 0,
    canGoBack: () => false,
    goBack: () => void 0,
    isForward: true,
    notifyTransitionComplete: () => void 0,
    notifyTransitionExit: () => void 0,
    transitionMode: "in-out",
});

if (__DEV__) {
    RouteContext.displayName = "RouteContext";
}
